import { graphql } from 'client/gql';
import { GroupType } from 'client/gql/graphql';
import { GroupEditorsModal } from 'components/Groups/modals/GroupEditorsModal';
import { GroupViewersModal } from 'components/Groups/modals/GroupViewersModal';
import { CooperationGroupsTable } from 'components/Groups/tables/CooperationGroupsTable';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { updateClaims } from '../../../utils/authStore';

const SettingsCollaborationGroupsSubpageQueryDocument = graphql(`
  query SettingsCollaborationGroupsSubpageData(
    $groupWhere: GroupWhere!
    $calendarEventWhere: CalendarEventWhere!
    $organizationUuid: ID!
    $groupUuid: ID!
  ) {
    ...UseGroupsTableFields
    ...GroupEditorsFormFields
    ...GroupViewersFormFields
  }
`);

export type GroupSettings = {
  cloudDirectory: boolean;
  asThreemaGroup: boolean;
  allowViewersCanAddMaterial: boolean;
  allowViewersCanCreateEvents: boolean;
  allowViewersCanStartMeeting: boolean;
};

export const SettingsCollaborationGroupsSubpage = () => {
  const { pimAuthClaims } = useAuthClaims();
  const navigate = useNavigate();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);
  const [editorsModalOpen, setEditorsModalOpen] = useState<boolean>(false);
  const [viewersModalOpen, setViewersModalOpen] = useState<boolean>(false);
  const [groupUuid, setGroupUuid] = useState<string | null>(null);

  const [{ data }] = useQuery({
    query: SettingsCollaborationGroupsSubpageQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
      schoolYearUuid: '',
      groupUuid: groupUuid ?? '',
      groupWhere: {
        type: GroupType.Group,
        isCommunity: true,
        AND: [
          {
            organization_NOT: { uuid: organizationUuid },
          },
          {
            admins_SOME: { organization: { uuid: organizationUuid } },
          },
        ],
      },
      calendarEventWhere: {},
    },
  });

  const permissions = usePermissionChecker();

  function handleEditorsEdit(uuid: string) {
    setEditorsModalOpen(true);
    setGroupUuid(uuid);
  }

  function handleViewersEdit(uuid: string) {
    setViewersModalOpen(true);
    setGroupUuid(uuid);
  }

  async function handleClose() {
    await updateClaims(() => setClaimsTrigger(!claimsTrigger));
    setEditorsModalOpen(false);
    setViewersModalOpen(false);
    setGroupUuid(null);
  }

  function handleNavigate(uuid: string) {
    navigate('/groups/' + uuid);
  }

  return (
    <BpSubpage isForbidden={!permissions?.canUpdateCollaborationGroups() || !pimAuthClaims.getOmniOrganization()}>
      <CooperationGroupsTable
        query={data}
        permissions={permissions}
        onNavigate={handleNavigate}
        onEditorsEdit={handleEditorsEdit}
        onViewersEdit={handleViewersEdit}
      />

      {groupUuid && (
        <GroupEditorsModal
          isOpen={editorsModalOpen}
          groupUuid={groupUuid}
          schoolYearUuid={''}
          onClose={handleClose}
          query={data}
        />
      )}

      {groupUuid && (
        <GroupViewersModal
          isOpen={viewersModalOpen}
          groupUuid={groupUuid}
          schoolYearUuid={''}
          onClose={handleClose}
          query={data}
        />
      )}
    </BpSubpage>
  );
};
